import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import moment from 'moment';
import 'moment/locale/tr';
import { KTSVG } from 'src/_metronic/helpers';

const API = `${process.env.REACT_APP_API_URL}`;

interface AnnouncementData {
  id: number;
  title: string;
  content: string;
  priority: string;
  sent_at: string;
  created_by_name: string;
}

const PRIORITY_COLORS: Record<string, { color: string; label: string }> = {
  low: { color: '#A1A5B7', label: 'Düşük' },
  normal: { color: '#009EF7', label: 'Normal' },
  high: { color: '#FFA800', label: 'Yüksek' },
  urgent: { color: '#F1416C', label: 'Acil' },
};

export default function AnnouncementModal() {
  const [announcements, setAnnouncements] = useState<AnnouncementData[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchUnread = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin-announcements/unread`);
      if (res.data && res.data.length > 0) {
        setAnnouncements(res.data);
        setCurrentIndex(0);
        setVisible(true);
        // Mark all as seen
        for (const ann of res.data) {
          try {
            await axios.post(`${API}/admin-announcements/mark-seen/${ann.id}`);
          } catch {
            /* ignore */
          }
        }
      }
    } catch {
      // sessizce devam
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchUnread();
    }, 3500); // Release note modaldan biraz sonra
    return () => clearTimeout(timer);
  }, [fetchUnread]);

  const current = announcements[currentIndex];

  const handleRead = async () => {
    if (!current) return;
    try {
      setLoading(true);
      await axios.post(`${API}/admin-announcements/mark-read/${current.id}`);

      if (currentIndex < announcements.length - 1) {
        setCurrentIndex((prev) => prev + 1);
      } else {
        setVisible(false);
        setAnnouncements([]);
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  const handleDismissAll = async () => {
    try {
      setLoading(true);
      for (const ann of announcements) {
        try {
          await axios.post(`${API}/admin-announcements/mark-read/${ann.id}`);
        } catch {
          /* ignore */
        }
      }
      setVisible(false);
      setAnnouncements([]);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  if (!visible || !current) return null;

  const pr = PRIORITY_COLORS[current.priority] || PRIORITY_COLORS.normal;

  return (
    <div
      className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
      style={{ zIndex: 10000, backgroundColor: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)' }}
    >
      <style>{`
        @keyframes annSlideIn { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
        .ann-modal-body p { margin-bottom: 0.5rem; }
        .ann-modal-body ul, .ann-modal-body ol { padding-left: 1.4rem; margin-bottom: 0.5rem; }
        .ann-modal-body img { max-width: 100%; border-radius: 6px; margin: 0.5rem 0; }
        .ann-modal-body table { width: 100%; border-collapse: collapse; margin: 0.5rem 0; }
        .ann-modal-body td, .ann-modal-body th { border: 1px solid #e1e3ea; padding: 0.4rem 0.6rem; font-size: 0.9rem; }
        .ann-modal-body h2, .ann-modal-body h3, .ann-modal-body h4 { margin-top: 0.6rem; margin-bottom: 0.4rem; font-weight: 700; }
      `}</style>

      <div
        className="bg-white rounded-3 shadow-lg position-relative d-flex flex-column"
        style={{
          width: '95%',
          maxWidth: 640,
          maxHeight: '90vh',
          animation: 'annSlideIn 0.3s ease-out',
        }}
      >
        {/* Header */}
        <div className="d-flex align-items-center justify-content-between px-6 py-4" style={{ borderBottom: `3px solid ${pr.color}` }}>
          <div className="d-flex align-items-center">
            <span className="me-3" style={{ color: pr.color }}>
              <KTSVG path="/media/icons/duotone/General/Notifications1.svg" className="svg-icon-2hx" />
            </span>
            <div>
              <h4 className="mb-0 fw-bolder">{current.title}</h4>
              <div className="text-muted fs-8 mt-1">
                {current.created_by_name} · {moment(current.sent_at).format('DD MMMM YYYY, HH:mm')}
                {current.priority !== 'normal' && (
                  <span className="ms-2 badge" style={{ background: pr.color + '20', color: pr.color, fontSize: '0.7rem' }}>
                    {pr.label}
                  </span>
                )}
              </div>
            </div>
          </div>
          {announcements.length > 1 && (
            <span className="badge badge-light-primary fs-8 fw-bold">
              {currentIndex + 1} / {announcements.length}
            </span>
          )}
        </div>

        {/* Content */}
        <div className="px-6 py-5 flex-grow-1" style={{ overflowY: 'auto' }}>
          <div className="ann-modal-body" dangerouslySetInnerHTML={{ __html: current.content }} />
        </div>

        {/* Footer */}
        <div className="d-flex align-items-center justify-content-between px-6 py-4 border-top">
          {announcements.length > 1 ? (
            <button className="btn btn-sm btn-light text-muted" onClick={handleDismissAll} disabled={loading}>
              Tümünü Kapat
            </button>
          ) : (
            <div />
          )}
          <button className="btn btn-sm btn-primary px-6" onClick={handleRead} disabled={loading}>
            {loading && <span className="spinner-border spinner-border-sm me-2" />}
            {currentIndex < announcements.length - 1 ? 'Okudum, Sonraki →' : 'Okudum, Kapat'}
          </button>
        </div>
      </div>
    </div>
  );
}
